import {Button, Flex, Text, VStack} from '@chakra-ui/react';
import {capitalizeFirstLetter, routes, useSearchClient} from 'ff-common';
import {useNavigate} from 'react-router-dom';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {useSearch} from './SearchContext';
import {useMemo} from 'react';
import {OperationCommercialeListeView} from '@/domains/operation_commerciale/OperationCommercialeListeView';
import {deburr} from 'lodash-es';

/**
 * Sidebar gauche de la recherche.
 * Affiche les catégories dont le libellé contient le mot recherché,
 * puis les opérations commerciales "En ce moment".
 */
export const SearchSidebar: React.FC = () => {
  const search = useSearchClient();
  const nomenclatureWeb = search.getNomenclatureWeb();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();
  // liveTerm : terme recherché pour le highlight
  const {liveTerm} = useSearch();

  // Catégories dont le libellé contient le mot recherché
  const matchingCategories = useMemo(() => {
    if (!nomenclatureWeb || !liveTerm) return [];
    const termeRecherche = deburr(liveTerm.toLowerCase()); // On utilise deburr pour ignorer les accents dans la recherche
    const matching = nomenclatureWeb.filter((cat) =>
      deburr(cat.libelle.toLowerCase()).includes(termeRecherche),
    );
    // Dédupliquer par libellé (des catégories différentes peuvent avoir le même nom)
    const libellesDejaVus = new Set<string>();
    return matching.filter((cat) => {
      const key = cat.libelle.toLowerCase();
      if (libellesDejaVus.has(key)) return false;
      libellesDejaVus.add(key);
      return true;
    });
  }, [nomenclatureWeb, liveTerm]);

  // Met en surbrillance la partie du libellé qui correspond au terme recherché.
  // Ex: highlightMatch("crèmerie") avec liveTerm="crème" → "Crèmerie" avec "Crème" en bleu.
  // Recherche brute-force sur les sous-chaînes : gère naturellement les cas où deburr
  // change la longueur (ex: "œ" → "oe") sans mapping de positions explicite.
  const highlightMatch = (libelle: string) => {
    const capitalized = capitalizeFirstLetter(libelle);
    const termeRecherche = deburr(liveTerm.toLowerCase());
    if (!termeRecherche) return capitalized;

    for (let i = 0; i < capitalized.length; i++) {
      for (let j = i + 1; j <= capitalized.length; j++) {
        const normalized = deburr(capitalized.slice(i, j).toLowerCase());
        if (normalized === termeRecherche) {
          // Le <span> parent est nécessaire pour préserver les espaces dans un <Button> flex
          return (
            <span>
              {capitalized.slice(0, i)}
              <Text
                as='span'
                color='blue.main'
              >
                {capitalized.slice(i, j)}
              </Text>
              {capitalized.slice(j)}
            </span>
          );
        }
        if (normalized.length > termeRecherche.length) break;
      }
    }
    return capitalized;
  };

  // Lors du clic sur une catégorie, on navigue vers la page de catalogue de la catégorie.
  const handleCategoryClick = (catWebId: string) => {
    navigate(
      buildClientRoute(routes.client.catalogue, {}, {cat_web: catWebId}),
    );
  };

  return (
    <VStack
      align='stretch'
      spacing={6}
    >
      {/* Catégories matchant la recherche */}
      <Flex
        flexWrap='wrap'
        gap={2}
      >
        {matchingCategories.map((cat) => (
          <Button
            key={cat.id}
            variant='outline'
            bg='gray.extra_light'
            color='gray.dark'
            borderWidth={1}
            borderColor='gray.200'
            onClick={() => {
              handleCategoryClick(cat.id);
            }}
            size='sm'
            borderRadius='full'
            whiteSpace='normal'
            textAlign='left'
            minW='auto'
            px={4}
            _hover={{bg: 'gray.light_bg', borderColor: 'gray.400'}}
          >
            {highlightMatch(cat.libelle)}
          </Button>
        ))}
      </Flex>

      {/* Opérations commerciales "En ce moment" */}
      <OperationCommercialeListeView context='search_sidebar' />
    </VStack>
  );
};
